<template>
  <div class="SimpleBarChart">
    <dl :style="`--color: ${color}; height: ${height}px`">
      <dt ref="title">
        <span>
          {{ label }} 
          <transition name="fade">
            <span v-if="filteredBy" :style="`color: ${filterColor}`">{{ relationLabel }} {{ filteredBy }}</span>
          </transition>
        </span>
          
        <span v-if="canExpand" class="SimpleBarChart__Toggle" @click="handleToggle">
          ({{ limitActive ? 'Expand' : 'Collapse' }})
        </span>
      </dt>
      <transition-group name="fade">
        <dd 
          v-for="row in preppedRows" 
          :key="row.label" 
          class="dataset"
          :class="{'dataset--filtered': filteredBy !== ''}" 
          :style="`--total: ${row.value}; --filtered: '${row.filter} / ${row.value}'; --width: ${row.filter / row.value * 100}%`"
          @click="handleClick({ row })">
          <span class="text">{{ row.label }}</span>
          <span 
            class="bar" 
            :class="{ disabled: row.disabled }" 
            :style="{ width: `${ startAnimation ? row.width : 0  }%` }"
            :content="tooltip({ row })" 
            v-tippy="{ followCursor : true }"></span>
        </dd>
      </transition-group>
    </dl>

  </div>
</template>

<script>
export default {
  props: {
    label: {
      type: String,
      default: 'Chart label'
    },
    limit: {
      type: Number,
      default: 5
    },
    filteredBy: {
      type: String,
      default: ''
    },
    filterColor: {
      type: String,
      default: '#1ECDA4'
    },
    rows: {
      type: Array,
      default: function() {
        return [{
          label: 'A',
          value: 4,
          filter: 0,
          disabled: false
        }, {
          label: 'B',
          value: 2,
          filter: 0,
          disabled: false
        }]
      }
    },
    color: {
      type: String,
      default: '#1ECDA4'
    },
    relationLabel: {
      type: String,
      default: 'in'
    },
    sortBy: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      startAnimation: false,
      expanded: false,
      limitActive: true,
      titleHeight: 28,
      orderedRows: []
    }
  },
  computed: {
    total() {
      return this.rows.reduce((total, row) => {
        return total + parseInt(row.value)
      }, 0)
    },
    max() {
      let list = this.orderedRows

      if (this.limitActive && this.canExpand) {
        list = list.slice(0, this.limit - 1)
        let other = this.orderedRows.slice(this.limit - 1).reduce((other, row) => {
          return {
            value: other.value + row.value
          }
        }, {
          value: 0
        })
        list.push(other)
      }

      return list.reduce((max, row) => {
        let val = parseInt(row.value)
        return val > max ? val : max
      }, 0)

    },
    height() {
      return this.limitActive ? 
        (40 + this.titleHeight + Math.min(this.limit, this.rows.length) * 40) : 
        (40 + this.titleHeight + this.rows.length * 40)
    },
    canExpand() {
      return this.rows.length > this.limit
    },
    preppedRows() {
      let list = this.orderedRows.map((row) => {
        row.percentage = (row.value / this.total) * 100
        row.width = Math.min(75, (row.value / this.max * 100) * 0.75)
        return row
      })

      if (this.limitActive && this.canExpand) {
        let other = this.orderedRows.slice(this.limit - 1).reduce((other, row) => {
          return {
            label: other.label,
            value: other.value + row.value,
            percentage: ((other.value + row.value) / this.total * 100),
            width: Math.min(75, ((other.value + row.value ) / this.max * 100) * 0.75),
            filter: 'Click to expand',
            disabled: row.disabled
          }
        }, {
          label: 'Other',
          value: 0,
          percentage: 0,
          width: 0,
          filter: 0,
          disabled: false
        })

        list = list.splice(0, this.limit - 1)
        list.push(other)
      }

      return list
    }
  },
  watch: {
    filteredBy() {
      this.$nextTick(() => {
        this.titleHeight = this.$refs.title.clientHeight;
        this.sortRows()
      })
    },
    rows() {
      this.sortRows()
    }
  },
  mounted() {
    this.sortRows()

    if (this.$refs.title) {
      this.titleHeight = this.$refs.title.clientHeight;
    }
    this.$nextTick(() => {
      this.startAnimation = true
    })
       
  },
  methods: {
    sortRows() {
      let rows = this.rows.slice()

      if (this.filteredBy && this.sortRows && rows.length && (this.sortBy in rows[0])) {
        this.orderedRows = rows.sort((a, b) => b.label === 'Other' 
          ? -1 : (a.label === 'Other' ? 1 
          : a[this.sortBy] === b[this.sortBy] 
            ? a.value < b.value ? 1 : -1
            : a[this.sortBy] < b[this.sortBy])) ? 1 : -1
      } else if (rows.length) {
        this.orderedRows = rows.sort((a, b) => b.label === 'Other' ? -1 : (a.label === 'Other' ? 1 : (a.value < b.value ? 1 : -1)))
      } else {
        this.orderedRows = []
      }
    },
    handleClick({ row }) {
      if (row.label === 'Other') {
        if (this.limitActive) {
          this.handleToggle()
        }
      } else {
        this.$emit('click', { row })
      }
    },
    handleToggle() {
      this.limitActive = ! this.limitActive
      this.$emit('expand', { status: this.limitActive })
    },
    /**
     * Shows percentage values. 
     */
    tooltip({ row }) {
      if (this.filteredBy) {
        if (row.label === 'Other' && this.limitActive) {
          return `Click to expand the list`
        } else {
          return `${ (row.filter / row.value * 100).toFixed(0)}% of ${row.value} and ${ (row.filter / this.total * 100).toFixed(0)}% of ${this.total}, `
        }
      }
      let tooltip = `${row.percentage.toFixed(0)}% of ${this.total}`
      if (row.label === 'Other' && this.limitActive) {
        tooltip = `${tooltip} (click to expand)`
      }
      return tooltip
    }
  }
}
</script>

<style lang="scss" scoped>

.fade-enter-active, .fade-leave-active {
  transition: opacity .5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.SimpleBarChart {
  padding-bottom: 20px;
  user-select: none;
  background-color: white;

  &__Toggle {
    display: inline-block;
    cursor: pointer;
    font-size: 14px;
    font-weight: normal;
    color: lighten(black, 20%);
    margin-left: 5px;
    min-width: 65px;
  }
}

dl {
  background-color: white;
  flex-direction: column;
  width: 100%;
  max-width: 900px;
  position: relative;
  padding: 20px;
  overflow: hidden;
  transition: height 0.4s ease;
}

dt {
  align-self: flex-start;
  width: calc(100% - 150px);
  display: flex;
  text-align: center;
  font-size: 1.2em;
  font-weight: 700;
  margin-bottom: 20px;
  margin-left: 150px;
  align-items: center;
  // justify-content: center;
}

.text {
  font-weight: 600;
  display: flex;
  align-items: center;
  height: 40px;
  width: 150px;
  background-color: white;
  position: absolute;
  left: 0;
  justify-content: flex-end;
  text-align: right;
}

.dataset {
  font-size: .8em;
  line-height: 1;
  text-transform: uppercase;
  width: calc(100% - 150px);
  height: 40px;
  margin-left: 150px;
  margin-bottom: 0;
  padding-right: 40px;
  background: repeating-linear-gradient(
    to right,
    #ddd,
    #ddd 1px,
    #fff 1px,
    #fff 5%
  );
  

  .bar {
    display: block;
    background-color: var(--color);
    width: 0%;
    margin-bottom: 10px;
    height: 60%;
    position: relative;
    top: 50%;
    transform: translateY(-50%);
    transition: background-color .8s ease, width 1s ease;
    cursor: pointer;
    z-index: 1;
    outline: none;
    outline-style: none;
    outline-color: transparent;

    counter-reset: total var(--total);

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      opacity: 0;
      transition: opacity 0.5s ease-in-out, width 0.8s ease;
      z-index: -1;
      // background: linear-gradient(0deg, rgba(0,187,198,1) 0%, rgba(0,206,162,1) 100%);
      background: rgba(255,255,255,0.4)
    }

    &:after {
      content: counter(total);
      position: absolute;
      right: -1.5em;
      top: 50%;
      transform: translate(50%, -50%);
      font-size: 16px;
      min-width: 40px;
      text-align: center;
    }

    &:hover {
      &:before {
        opacity: 1;
      }
    }

    &.disabled {
      background-color: #E8EAF1;

      &:hover {
        &:before {
          opacity: 0;
        }
      }
    }
  }


  &--filtered {
    .bar {
      background-color: #E8EAF1;

      &:before {
        width: var(--width);
        background-color: var(--color);
        opacity: 1;
      }

      &:after {
        right: -2em;
        content: var(--filtered);
      }
    }
  }
}

</style>